import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useSession } from '@/components/auth/session';
import { Section, SectionHead, Surface } from '@/components/ui/section';
import {
  burstHeadline,
  detailFor,
  groupIntakeBursts,
  milestoneColor,
  milestones,
} from '@/lib/activity';
import type { FeedItem } from '@/lib/feed';
import type { FleetFeed } from '@/lib/feed-context';
import { ago, minutesSince } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * What happened while you were away, in the sense a person means it.
 *
 * This is the third of the three questions PRODUCT.md sets, and the old rail
 * answered it with the dispatcher's log: a dozen offers, expiries and holds, each
 * carrying a colleague's email address and how much of their Claude window was
 * left. See `lib/activity.ts` for why that is three problems rather than a busy
 * list.
 *
 * What is left is the milestones, one line each: a verb, what it happened to, and
 * how long ago. Nothing wraps, so the column can be read down its left edge in
 * the two seconds a person spends on it. The full record, machinery included, is
 * the admin audit log, which is a page built for reading a record rather than a
 * strip built for catching up.
 *
 * The list freezes while the pointer is inside it, as the old rail did: a list
 * that re-sorts under a reading cursor is a list nobody can read.
 */
export function RecentActivity({
  items,
  scope = 'fleet',
  className,
  limit = 8,
  /** Overrides the "across the fleet" clause where the feed is narrower than
      the fleet: the project page passes its repo, and saying otherwise there
      would be a caption describing a different list. */
  meta,
}: {
  items: FeedItem[];
  scope?: FleetFeed['scope'];
  className?: string;
  limit?: number;
  meta?: string;
}) {
  const { session } = useSession();
  const [frozen, setFrozen] = useState<FeedItem[] | null>(null);
  const source = frozen ?? items;
  // Intake bursts fold to one line each before the limit is applied, so a
  // schedule that files a dozen jobs at once costs the strip one line rather
  // than the whole thing (issue #30).
  const shown = groupIntakeBursts(milestones(source)).slice(0, limit);

  return (
    <Section className={className}>
      <SectionHead
        title="Recently"
        meta={meta ?? (scope === 'mine' ? 'your jobs and devices' : 'across the fleet')}
        actions={
          session.isAdmin ? (
            <Link
              to="/admin/audit"
              className="text-fg-3 hover:text-foreground text-xs transition-colors duration-100"
            >
              Full audit log →
            </Link>
          ) : null
        }
      />
      <Surface
        onMouseEnter={() => setFrozen(items)}
        onMouseLeave={() => setFrozen(null)}
        aria-label="Recent activity"
      >
        {shown.length ? (
          <ul className="divide-line divide-y">
            {shown.map((item) => {
              // A burst is not any one of the events it stands for, so it gets
              // no key and no per-job detail - the count in the headline is
              // the whole story.
              const detail = item.count > 1 ? { key: null, rest: '' } : detailFor(item);
              return (
                <li
                  key={item.id}
                  className={cn(
                    'flex items-baseline gap-3 px-gutter py-2.5',
                    item.fresh && 'dy-enter',
                  )}
                >
                  <span
                    className="size-1.5 shrink-0 translate-y-[-1px] rounded-full"
                    style={{ background: milestoneColor(item.kind) }}
                    aria-hidden
                  />
                  {/* Fixed width, so the verbs line up and the column can be read
                      straight down rather than followed word by word. */}
                  <span className="text-foreground shrink-0 truncate text-base font-medium sm:w-[8.5rem]">
                    {burstHeadline(item)}
                  </span>
                  {detail.key ? (
                    <span className="text-fg-2 tnum shrink-0 font-mono text-xs">{detail.key}</span>
                  ) : null}
                  <span className="text-fg-3 min-w-0 flex-1 truncate text-sm">{detail.rest}</span>
                  {/* Relative, not clock time: this is read as "what happened while
                      I was away", and an age is monotonic down the list where a
                      wall clock crossing midnight is not. */}
                  <time
                    className="text-fg-3 tnum shrink-0 text-xs whitespace-nowrap"
                    dateTime={item.at}
                  >
                    {ago(minutesSince(item.at))}
                  </time>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-fg-3 px-gutter py-7 text-center text-sm">
            {scope === 'mine'
              ? 'Nothing yet. When a job you opened moves, or one of your agents finishes something, it lands here.'
              : 'Nothing yet. Plans, pull requests and finished work land here as they happen.'}
          </p>
        )}
      </Surface>
    </Section>
  );
}
